'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/container';
import { cn, parseHeading, sanitizeHtml } from '@/lib/utils';
import { DirectusFile } from '@/types/directus-schema';
import { getDirectusAssetURL } from '@/lib/directus/directus-utils';
import { setAttr } from '@directus/visual-editing';

import ButtonGroup from '@/components/blocks/ButtonGroup';

interface SimpleHeroProps {
	data: {
		headline?: string;
		tagline?: string;
		background_type?: 'image' | 'blue' | 'orange' | 'dark';
		background_image?: DirectusFile;
		alignment?: 'center' | 'left' | 'right';
		button_group?: {
			id: string;
			buttons: any[];
		};
	};
	itemId?: string;
}

const SimpleHero = ({ data, itemId }: SimpleHeroProps) => {
	const { headline, tagline, background_type = 'image', background_image, alignment = 'center', button_group } = data;

	// Background logic
	const bgColorClass =
		background_type === 'blue'
			? 'bg-blue'
			: background_type === 'orange'
				? 'bg-orange'
				: background_type === 'dark'
					? 'bg-gray-900'
					: 'bg-gray-900'; // Default fallback for image if it fails loading

	const bgType =
		background_type === 'blue'
			? 'blue'
			: background_type === 'orange'
				? 'orange'
				: background_type === 'dark' || background_type === 'image'
					? 'dark'
					: 'light';

	// Only check for image if type is image
	const showImage = background_type === 'image' && background_image;

	return (
		<section
			className={cn(
				'relative flex items-center overflow-hidden h-[450px]', // Reduced height
				bgColorClass,
			)}
		>
			{/* Background Image (if enabled and set) */}
			{showImage && (
				<div className="absolute inset-0 z-0">
					<Image
						src={getDirectusAssetURL(background_image)}
						alt={background_image.description || background_image.title || headline || 'Hero background'}
						fill
						className="object-cover" // Full opacity so radial gradient stands out
						sizes="100vw"
						priority
					/>
					{/* Radial gradient overlay for better text contrast without dimming the whole image */}
					<div
						className={cn(
							'absolute inset-0',
							alignment === 'center'
								? 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-black/80 via-black/40 to-transparent'
								: alignment === 'right'
									? 'bg-[radial-gradient(ellipse_at_right,_var(--tw-gradient-stops))] from-black/90 via-black/50 to-transparent'
									: 'bg-[radial-gradient(ellipse_at_left,_var(--tw-gradient-stops))] from-black/90 via-black/50 to-transparent',
						)}
					/>
				</div>
			)}

			<div className="relative z-10 w-full">
				<Container>
					<div
						className={cn(
							'max-w-3xl space-y-6 text-white',
							alignment === 'center'
								? 'mx-auto text-center'
								: alignment === 'right'
									? 'ml-auto text-right md:mr-12'
									: 'text-left md:ml-12',
						)}
					>
						{headline && (
							<h1
								className="text-4xl md:text-5xl font-heading"
								{...(itemId
									? {
											'data-directus': setAttr({
												collection: 'block_simple_hero',
												item: itemId,
												fields: ['headline'],
											}),
										}
									: {})}
							>
								{parseHeading(headline)}
							</h1>
						)}

						{tagline && (
							<div
								className={cn(
									'text-lg md:text-xl text-gray-100 prose prose-invert max-w-none',
									alignment === 'center' ? 'text-center [&>*]:mx-auto' : '',
								)}
								{...(itemId
									? {
											'data-directus': setAttr({
												collection: 'block_simple_hero',
												item: itemId,
												fields: ['tagline'],
											}),
										}
									: {})}
								dangerouslySetInnerHTML={{ __html: tagline }}
							/>
						)}

						{button_group && (
							<div
								className="pt-4"
								{...(itemId
									? {
											'data-directus': setAttr({
												collection: 'block_simple_hero',
												item: itemId,
												fields: ['button_group'],
											}),
										}
									: {})}
							>
								<ButtonGroup
									buttons={button_group.buttons?.map((b: any) => b.block_button_id).filter(Boolean)}
									theme={bgType as any}
									className={
										alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'
									}
								/>
							</div>
						)}
					</div>
				</Container>
			</div>
		</section>
	);
};

export default SimpleHero;
